/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Radio, RadioGroup } from '@pwc/appkit-react';
import {
  hasPermissionToAccess,
  PERMISSION_TYPE,
  PERMISSION,
} from '@common-packages/customer-permissions-utils';

import { dateFormatter } from '../../shared/formatters';
import config from '../../config';
import { SSN_MODAL_OPTION } from '../../shared/enums/pdf';
import { useQueryCommonDataModels } from '../../shared/queries/commonDataModels';
import { useQueryCommonDataModelFields } from '../../shared/queries/stateCommonInformation';
import { globalContextSelector } from '../../shared/store/selectors';
import { customerPermissionsSelector } from '../../shared/store/selectors';

import styles from './downloadPdfPanel.module.scss';

interface DownloadRegeneratePdfParams {
  handleRegenerateReturn: () => Promise<void>;
  latestReturnPdfGenerationDate?: string | null;
  isDownloading: boolean;
  handleDownloadPdfClick: () => void;
  shouldRevealSSNOption: string;
  setShouldRevealSSNOption: React.Dispatch<React.SetStateAction<string>>;
  isIndividualPartnersAndHasPermission: boolean;
}
interface CommonDataModel {
  dataModelId: string;
}

const DownloadRegeneratePdf = ({
  handleRegenerateReturn,
  latestReturnPdfGenerationDate,
  isDownloading,
  handleDownloadPdfClick,
  shouldRevealSSNOption,
  setShouldRevealSSNOption,
  isIndividualPartnersAndHasPermission,
}: DownloadRegeneratePdfParams) => {
  const handleOptionChange = useCallback(
    (e: Event, value: string) => {
      console.log('Checking if the file is bGetUnencryptedPDF: ', value);
      setShouldRevealSSNOption(value);
    },
    [setShouldRevealSSNOption],
  );

  const globalContext = useSelector(globalContextSelector);
  const customerPermissions = useSelector(customerPermissionsSelector);
  const {
    taxYear,
    period,
    filingTypeId,
    jurisdictionId,
    entityId,
    consolidationId,
  } = globalContext;
  const [, setDataModelsOptions] = useState<any[]>([]);
  const [selectedDataModel, setSelectedDataModel] = useState<CommonDataModel | null>(null);

  const permissionForBankData = hasPermissionToAccess({
    customerPermissions,
    permissionType: PERMISSION_TYPE.EDIT,
    routeDefinitions: [PERMISSION.BANK_DATA],
  });

  const [shouldShowSSN, setShouldShowSSN] = useState(isIndividualPartnersAndHasPermission);
  const [shouldShowBankingData, setShouldShowBankingData] = useState(false);
  const [shouldShowCombinedOption, setShouldShowCombinedOption] = useState(false);

  const { data: commonDataModels = [] } = useQueryCommonDataModels({
    params: { taxYear, jurisdictionId },
    enabled: Boolean(taxYear && jurisdictionId),
  }) as { data: any[] };
  useEffect(() => {
    const options = commonDataModels.map(dataModel => ({
      ...dataModel,
      value: dataModel.dataModelId,
      label: dataModel.description ? dataModel.description : dataModel.name,
    }));
    setDataModelsOptions(options);
    setSelectedDataModel(options[0]);
  }, [commonDataModels]);
  const { data: dataModelFieldsResponse } = useQueryCommonDataModelFields({
    params: {
      period,
      businessEntityId: filingTypeId === 'SEPARATE' ? entityId : consolidationId,
      dataModelId: selectedDataModel?.dataModelId || '',
      filingTypeId,
    },
    enabled: Boolean(period && entityId && selectedDataModel),
  }) as { data: any[] };
  const dataModelFields = useMemo(
    () => (dataModelFieldsResponse || []).map((field, rowId) => ({ ...field, rowId })),
    [dataModelFieldsResponse],
  );
  useEffect(() => {
    if (!dataModelFields || dataModelFields.length === 0) {
      return;
    }
    const accountNumberField = dataModelFields.find(field => field.aliasName === 'ACCOUNTNUMBER');
    const routingNumberField = dataModelFields.find(field => field.aliasName === 'ROUTINGNUMBER');
    if (accountNumberField || routingNumberField) {
      if (
        accountNumberField?.shouldMaskBankData === true ||
        routingNumberField?.shouldMaskBankData === true
      ) {
        //have only banking data
        if (permissionForBankData && !isIndividualPartnersAndHasPermission) {
          setShouldShowCombinedOption(false);
          setShouldShowSSN(false);
          setShouldShowBankingData(true);
        }
        //have both but missing permissionForBankData
        if (!permissionForBankData && isIndividualPartnersAndHasPermission) {
          setShouldShowCombinedOption(false);
          setShouldShowSSN(false);
          setShouldShowBankingData(false);
        }
        if (permissionForBankData && isIndividualPartnersAndHasPermission) {
          setShouldShowCombinedOption(true);
          setShouldShowSSN(false);
          setShouldShowBankingData(false);
        }
        console.log('shouldShowBankingData ', shouldShowBankingData);
        console.log('shouldShowSSN', shouldShowSSN);
        console.log('shouldShowCombinedOption', shouldShowCombinedOption);
      }
    }
    console.log('permissionForBankData on regenerate: ', permissionForBankData);
  }, [dataModelFields]);

  return (
    <div className={styles.panelWrapper}>
      <div className={styles.panelItemWrapper}>
        <img
          alt="validation-loading-icon"
          src={`${config.BASE_PATH}/assets/download-regenerate-pdf-graphic.png`}
          className={styles.illustration}
        />
      </div>
      {shouldShowCombinedOption && (
        <RadioGroup
          onChange={handleOptionChange}
          defaultValue={shouldRevealSSNOption}
          value={shouldRevealSSNOption}
          className={styles.radioOptionWarpper}
        >
          {SSN_MODAL_OPTION?.map((option, index) => (
            <Radio key={index} value={option.value}>
              {`${option.label} SSN and Banking Data`}
            </Radio>
          ))}
        </RadioGroup>
      )}
      {shouldShowSSN && (
        <RadioGroup
          onChange={handleOptionChange}
          defaultValue={shouldRevealSSNOption}
          value={shouldRevealSSNOption}
          className={styles.radioOptionWarpper}
        >
          {SSN_MODAL_OPTION?.map((option, index) => (
            <Radio key={index} value={option.value}>
              {`${option.label} SSN`}
            </Radio>
          ))}
        </RadioGroup>
      )}
      {shouldShowBankingData && (
        <RadioGroup
          onChange={handleOptionChange}
          defaultValue={shouldRevealSSNOption}
          value={shouldRevealSSNOption}
          className={styles.radioOptionWarpper}
        >
          {SSN_MODAL_OPTION?.map((option, index) => (
            <Radio key={index} value={option.value}>
              {`${option.label} Banking Data`}
            </Radio>
          ))}
        </RadioGroup>
      )}
      <span className={styles.text}>Here is your most up-to-date PDF.</span>
      <div className={styles.downloadButtonWrapper}>
        <Button
          size="xl"
          kind="secondary"
          className={styles.downloadButton}
          onClick={handleDownloadPdfClick}
          isLoading={isDownloading}
        >
          Download
        </Button>
        <span className={styles.pdfGenerationDateText}>
          Generated on{' '}
          {dateFormatter(latestReturnPdfGenerationDate, config.AMERICAN_DATE_TIME_FORMAT)}
        </span>
      </div>
      <span className={styles.text}>
        Re-generate to include changes and overwrite the previous PDF.
      </span>
      <Button size="xl" kind="secondary" onClick={handleRegenerateReturn} className={styles.button}>
        Re-Generate
      </Button>
    </div>
  );
};

export default DownloadRegeneratePdf;
